import { FC, useEffect, useState, useContext } from "react";
import { Table, Modal } from "antd";
import { ColumnsType } from "antd/es/table";
import { useModalAction } from "../../components/modal-views/context";
import { PlusIcon } from "src/components/icons/plus-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import { ResetIcon } from "src/components/icons/reset-icon";
import Button from "src/components/ui/button";
import SearchFilter_1 from "src/components/search-filters/user-management-filter_1";
import client from "src/api";
import { PermissionApiItem } from "src/api/types";
import GlobalContext from "src/context/global-context";
import { capitalizeFirstLetter, getErrorMessage } from "src/lib/utils";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  LIST_PAGE_SIZE,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

const PermissionsManagementPage: FC = () => {
  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.PERMISSIONS
  );
  const canViewPermissions = canView || canList;

  const { openModal } = useModalAction();
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);

  // State
  const [permissions, setPermissions] = useState<PermissionApiItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination & Filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PermissionApiItem | null>(null);
  const [permissionName, setPermissionName] = useState("");
  const [permissionDescription, setPermissionDescription] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);

  // Debounce search input and reset page to 1
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchPermissions = async (page: number, limit: number, search?: string) => {
    setLoading(true);
    try {
      const res = await client.permissions.getPermissions(page, limit, search, "name");
      const safeData = Array.isArray(res.data) ? res.data : [];
      setPermissions(safeData);
      setTotalItems(res.total || safeData.length);
    } catch (err: any) {
      setPermissions([]);
      setTotalItems(0);
      const errMsg = getErrorMessage(err?.response?.data || err?.data || err);
      if (err?.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        setAlertText(errMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewPermissions) {
      fetchPermissions(currentPage, pageSize, debouncedSearch);
    }
  }, [currentPage, pageSize, debouncedSearch, canViewPermissions]);

  const onPageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleOpenAddModal = () => {
    if (!canAdd) return;
    setEditingItem(null);
    setPermissionName("");
    setPermissionDescription("");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item: PermissionApiItem) => {
    if (!canEdit) return;
    setEditingItem(item);
    setPermissionName(item.name || "");
    setPermissionDescription(item.description || "");
    setIsModalOpen(true);
  };

  const handleDelete = (item: PermissionApiItem) => {
    if (!canDelete) return;
    openModal("GENERAL_DELETE", {
      callback: async () => {
        try {
          await client.permissions.deletePermission(item.id);
          setAlertText(`Permission "${item.name}" deleted successfully.`);
          setIsAlertOpened(true);
          fetchPermissions(currentPage, pageSize, debouncedSearch);
        } catch (error: any) {
          if (error.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else if (error.response?.data) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
          setIsAlertOpened(true);
        }
      },
      isLoading: () => false,
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingItem && !canEdit) return;
    if (!editingItem && !canAdd) return;

    if (!permissionName.trim()) {
      setAlertText("error_Please enter permission name!");
      setIsAlertOpened(true);
      return;
    }

    if (permissionDescription && permissionDescription.length > 500) {
      setAlertText("error_Description must not exceed 500 characters!");
      setIsAlertOpened(true);
      return;
    }

    setSubmitLoading(true);
    const payload = {
      name: permissionName.trim(),
      description: permissionDescription.trim(),
    };

    try {
      if (editingItem) {
        const res = await client.permissions.updatePermission(editingItem.id, payload);
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }
        setAlertText(`Permission "${permissionName.trim()}" updated successfully!`);
        setIsAlertOpened(true);
      } else {
        const res = await client.permissions.createPermission(payload);
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }
        setAlertText(`Permission "${permissionName.trim()}" created successfully!`);
        setIsAlertOpened(true);
      }
      setIsModalOpen(false);
      setPermissionName("");
      setPermissionDescription("");
      setEditingItem(null);
      fetchPermissions(currentPage, pageSize, debouncedSearch);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns: ColumnsType<PermissionApiItem> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      className: "w-auto sm:w-[100px]",
      render: (value: number) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Permission Name
        </p>
      ),
      dataIndex: "name",
      key: "name",
      className: "w-auto",
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {capitalizeFirstLetter(value || "")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Permission Key
        </p>
      ),
      dataIndex: "permission_key",
      key: "permission_key",
      className: "w-auto",
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value || ""}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Description
        </p>
      ),
      dataIndex: "description",
      key: "description",
      className: "w-auto min-w-[200px] max-w-[400px]",
      render: (value?: string | null) => (
        <div className="flex flex-row items-center">
          <p
            className="font-manrope text-secondary text-[14px] font-medium line-clamp-2"
            title={value || "-"}
          >
            {value ? value : "-"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      key: "actions",
      className: "w-[100px]",
      render: (_, record) => {
        if (!canEdit) {
          return (
            <p className="font-manrope text-gray-400 text-[20px] font-bold text-center">
              —
            </p>
          );
        }
        return (
          <div className="flex flex-row items-center gap-1">
            <button
              className="w-[30px] h-[30px] flex items-center justify-center hover:bg-gray rounded"
              onClick={() => handleOpenEditModal(record)}
              title="Edit Permission"
            >
              <EditIcon className="w-[20px] h-[20px] text-primary" />
            </button>
            {/* <button
              className="w-[30px] h-[30px] flex items-center justify-center hover:bg-gray rounded"
              onClick={() => handleDelete(record)}
              title="Delete Permission"
            >
              <DeleteIcon className="w-[20px] h-[20px] text-primary" />
            </button> */}
          </div>
        );
      },
    },
  ];

  if (!canViewPermissions) {
    return (
      <div className="w-full h-full bg-gray">
        <div className="w-full h-full flex-col p-2 sm:p-4">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Permissions Management
          </p>
          <div className="bg-white rounded-[12px] p-6 mt-4 text-center text-secondary">
            You do not have permission to view permissions.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col p-2 sm:p-4">
        {/* Header */}
        <div className="w-full flex flex-row items-center justify-between">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Permissions Management
          </p>
          <PermissionGate module={[permissionConstants.PERMISSIONS]} action="can_add">
            <div className="flex flex-row items-center gap-3">
              <button
                className="flex flex-row items-center justify-between px-2 sm:px-4 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC]"
                onClick={handleOpenAddModal}
              >
                <p className="font-manrope text-[14px] font-medium text-primary">
                  Add Permission
                </p>
                <PlusIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            </div>
          </PermissionGate>
        </div>

        {/* Search & Actions Toolbar */}
        <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <SearchFilter_1
            options={[{ code: "name", name: "Permission" }]}
            placeholder="Search by permission name"
            value="name"
            onChange={() => { }}
            searchText={searchQuery}
            setSearchText={setSearchQuery}
          />

          <div className="flex items-center gap-2">
            <button
              className="flex flex-row items-center justify-between px-3 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC] h-[44px] border border-light-300"
              onClick={() => fetchPermissions(currentPage, pageSize, debouncedSearch)}
            >
              <p className="font-manrope text-[14px] font-medium text-primary">
                Refresh
              </p>
              <ResetIcon className="w-[16px] h-[16px] text-primary" />
            </button>
          </div>
        </div>

        {/* Permissions Table */}
        <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={loading}
            showSorterTooltip={false}
            columns={columns}
            dataSource={permissions}
            pagination={{
              total: totalItems,
              current: currentPage,
              pageSize: pageSize,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
            rowKey="id"
          />
        </div>
      </div>

      {/* Create / Edit Permission Modal */}
      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setPermissionName("");
          setPermissionDescription("");
          setEditingItem(null);
        }}
        footer={null}
        title={
          <p className="text-[20px] font-manrope font-medium text-primary">
            {editingItem ? "Edit Permission" : "Add New Permission"}
          </p>
        }
      >
        <form onSubmit={handleFormSubmit} className="mt-4 flex flex-col gap-3 font-manrope">
          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Permission Name <span className="text-error">*</span>
            </p>
            <input
              type="text"
              className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0 focus:outline-none"
              placeholder="e.g. Dashboard, User Management, Reports"
              value={permissionName}
              onChange={(e) => setPermissionName(e.target.value)}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[14px] font-manrope font-medium text-secondary">
                Description
              </p>
              <span className="text-[12px] font-manrope text-secondary">
                {permissionDescription.length}/500
              </span>
            </div>
            <textarea
              rows={4}
              maxLength={500}
              className="placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2.5 text-primary focus:border-active focus:ring-0 focus:outline-none resize-none"
              placeholder="Enter permission description (max 500 characters)"
              value={permissionDescription}
              onChange={(e) => setPermissionDescription(e.target.value)}
            />
          </div>

          <div className="w-full flex flex-row items-center justify-end gap-2 mt-4 pt-4 border-t border-light-300">
            <Button
              variant="solid"
              type="button"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px] bg-[#BCBEAD] hover:bg-light-600"
              onClick={() => {
                setIsModalOpen(false);
                setPermissionName("");
                setPermissionDescription("");
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="validate"
              type="submit"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px]"
              disabled={submitLoading}
              isLoading={submitLoading}
            >
              {editingItem ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PermissionsManagementPage;
